import React, {useEffect, useState} from 'react';
import dayjs from "dayjs";
import CountUp from 'react-countup';
import {Button, Checkbox, FormControlLabel, Typography } from "@material-ui/core";
import NearMeOutlinedIcon from '@mui/icons-material/NearMeOutlined';

import {Application, ApplicationStatus} from "components/JobBoardDetails";
import {CollapsableContainer} from "components/CollapsableContainer";
import {InterviewStatusButton} from "./InterviewStatusButton";
import {CandidateDetails} from "./types";

import {
    ButtonsContainer,
    ButtonsRowContainer,
    CandidateDetailsContainer,
    CheckboxContainer,
    CheckboxLabel,
    Container,
    InfoButton,
    InfoSection,
    InfoSectionContent,
    InfoSectionItemTitle,
    PopupButton,
    ResumeButton,
    Score,
    ScoreContainer,
    ScoreLabel,
    ScoreSection,
    StyledApplicationsListItem,
    StyledAssignmentOutlinedIcon,
    StyledDescriptionOutlinedIcon,
    StyledDescriptionOutlinedIconWhite,
    StyledExpandMoreOutlinedIcon,
    StyledInfoOutlinedIcon,
    StyledThumbDownAltOutlinedIcon,
    StyledThumbUpAltOutlinedIcon,
    StyledWorkOutlineIcon,
    StyledNearMeOutlinedIcon,
    Description,
    SkillsTitle,
    SkillsContainer,
    SkillsLabel,
    WorkExperienceContainer,
    WorkExperienceEntry,
    JobTitle,
    CompanyName,
    JobDates,
    MatchScoreTooltipLink
} from './CandidateView.styles';

interface CandidateViewProps {
    application: Application;
    candidateDetails?: CandidateDetails;
    onScoreInfoButtonClick: () => void;
    onApplicationActionsPopupOpen: () => void;
    onRequestResume: () => void;
    onViewResume: () => void;
    onShortlistCandidate: () => Promise<void>;
    onDeclineCandidate: () => Promise<void>;
    onAllowShowInfoToggle: (newStatus: boolean) => void;
    allowShowInfo: boolean;
}

const CandidateView = ({
    candidateDetails,
    application,
    onScoreInfoButtonClick,
    allowShowInfo,
    onAllowShowInfoToggle,
    onRequestResume,
    onApplicationActionsPopupOpen,
    onViewResume,
    onDeclineCandidate,
    onShortlistCandidate,
}: CandidateViewProps) => {
    const [resumeVisible, setResumeVisible] = useState(false);

    useEffect(() => {
        setResumeVisible(!!application.resumeViewed);
    }, [application.resumeViewed]);

    return (
        <Container>
            <StyledApplicationsListItem
                application={application}
                isShortlisted={application.status === ApplicationStatus.shortlisted}
                isDeclined={application.status === ApplicationStatus.declined}
                isNew={application.status === ApplicationStatus.posted}
                candidateDetails={candidateDetails}
            />
            {candidateDetails && <CandidateDetailsContainer>
                {candidateDetails.score ? (
                    <ScoreSection $score={candidateDetails.score}>
                        <ScoreContainer>
                            <Score><CountUp end={candidateDetails.score} duration={3}/> %</Score>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                                <ScoreLabel>Match Score</ScoreLabel>
                                <MatchScoreTooltipLink 
                                    onClick={onScoreInfoButtonClick} 
                                >
                                    What's this?
                                </MatchScoreTooltipLink>
                            </div>
                        </ScoreContainer>
                    </ScoreSection>
                ) : null}
                {candidateDetails.workHistory ? (
                    <InfoSection>
                        <StyledWorkOutlineIcon/>
                        <InfoSectionContent>
                            <InfoSectionItemTitle>
                                History With Your Office
                            </InfoSectionItemTitle>
                            <div>{candidateDetails.workHistory}</div>
                        </InfoSectionContent>
                    </InfoSection>
                ) : null}
                {candidateDetails.interviewReliability ? (
                    <InfoSection>
                        <StyledAssignmentOutlinedIcon/>
                        <InfoSectionContent>
                            <InfoSectionItemTitle>
                                Reliability
                            </InfoSectionItemTitle>
                            <div>{candidateDetails.interviewReliability}</div>
                        </InfoSectionContent>
                    </InfoSection>
                ) : null}
                {candidateDetails.distanceInfo ? (
                    <InfoSection>
                        <StyledNearMeOutlinedIcon />
                        <InfoSectionContent>
                            <InfoSectionItemTitle>
                                Distance
                            </InfoSectionItemTitle>
                            <div>{candidateDetails.distanceInfo}</div>
                        </InfoSectionContent>
                    </InfoSection>
                ) : null}
            </CandidateDetailsContainer>}
            {resumeVisible ? (
                <>
                    <CheckboxContainer>
                        <FormControlLabel
                            className="no-fastclick"
                            control={<Checkbox
                                name="allowShowInfo"
                                checked={allowShowInfo}
                                onChange={() => onAllowShowInfoToggle(!allowShowInfo)}
                                icon={<img id="allowShowInfo" className="hidden-icon" alt=""
                                           src={"img/checkboxUnchecked.svg"}/>}
                                checkedIcon={<img id="officeCheckmark" className="hidden-icon" alt=""
                                                  src={"img/checkboxChecked.svg"}/>}
                            />} label={<CheckboxLabel>Allow {application.name} to see to your contact details</CheckboxLabel>}/>
                    </CheckboxContainer>
                    <ButtonsContainer>
                        <ResumeButton
                            variant="outlined"
                            onClick={onViewResume}
                            startIcon={<StyledDescriptionOutlinedIcon/>}
                            color="primary"
                            size="large"
                            id="view-resume-button"
                            className="bigButtonInNewStyle"
                        >
                            View Resume
                        </ResumeButton>
                    </ButtonsContainer>
                    <ButtonsRowContainer>
                        <PopupButton
                            variant="outlined"
                            onClick={onShortlistCandidate}
                            startIcon={<StyledThumbUpAltOutlinedIcon />}
                            color="primary"
                            size="large"
                            id="shorlist-button"
                            className="bigButtonInNewStyle"
                        >
                            Shortlist
                        </PopupButton>
                        <PopupButton
                            variant="outlined"
                            onClick={onDeclineCandidate}
                            startIcon={<StyledThumbDownAltOutlinedIcon />}
                            color="primary"
                            size="large"
                            id="decline-button"
                            className="bigButtonInNewStyle"
                        >
                            Decline
                        </PopupButton>
                    </ButtonsRowContainer>
                    <ButtonsContainer>
                        <InterviewStatusButton
                            interviewStatus={application.interviewStatus}
                            onApplicationActionsPopupOpen={onApplicationActionsPopupOpen}
                            endIcon={<StyledExpandMoreOutlinedIcon />}
                        />
                    </ButtonsContainer>
                </>
            ) : (
                <div>
                    <ButtonsContainer>
                        <Button
                            variant="contained"
                            onClick={onRequestResume}
                            startIcon={<StyledDescriptionOutlinedIconWhite/>}
                            color="primary"
                            size="large"
                            id="request-resume-button"
                            className="bigButtonInNewStyle"
                        >
                            View Candidate Profile
                        </Button>
                    </ButtonsContainer>
                </div>
            )}
            {application.skills && application.skills.length ? (
                <CollapsableContainer title="Skills">
                    <SkillsTitle>Training & Certifications received</SkillsTitle>
                    <SkillsContainer>
                        {application.skills.map((skill) => (
                            <div key={skill.id}>
                                <FormControlLabel
                                    className="no-fastclick"
                                    control={<Checkbox
                                    name="allowShowInfo"
                                    readOnly={true}
                                    checked={true}
                                    checkedIcon={<img id="officeCheckmark" className="hidden-icon" alt=""
                                                src={"img/checkboxChecked.svg"}/>}
                                />} label={<SkillsLabel>{skill.label}</SkillsLabel>}/>
                        </div>
                      ))}
                  </SkillsContainer>
                </CollapsableContainer>
              ) : null}
            {application.workExperience && application.workExperience.length ? (
                <CollapsableContainer title="Work Experience">
                    <WorkExperienceContainer>
                        {application.workExperience.map((position, index) => (
                            <WorkExperienceEntry key={`${position.jobTitle}-${index}`}>
                                <JobTitle>{position.jobTitle}</JobTitle>
                                <CompanyName>{position.companyName}</CompanyName>
                                <JobDates>
                                    {dayjs(position.fromDate).format('MMM YYYY')} - {dayjs(position.toDate).format('MMM YYYY')}
                                </JobDates>
                            </WorkExperienceEntry>
                        ))}
                    </WorkExperienceContainer>
                </CollapsableContainer>
            ) : null}
        </Container>
    );
}

export default CandidateView;
